<script lang="ts" setup>
import { computed, onMounted } from 'vue'
import { storeToRefs } from 'pinia'
import { useQuasar } from 'quasar'
import { DataExportStatus } from '@roadfootage/api-client'
import { useDataExportStore } from '../../stores/data-export'
import { formatDate, formatDateTime, formatFileSize } from '../../utils/format'

const $q = useQuasar()
const store = useDataExportStore()
const { latest, nextRequestAt, loaded, loadFailed, requesting, downloading } = storeToRefs(store)

const status = computed(() => latest.value?.status)
const preparing = computed(
  () => status.value === DataExportStatus.Pending || status.value === DataExportStatus.Building
)
const ready = computed(() => status.value === DataExportStatus.Ready)
const failed = computed(() => status.value === DataExportStatus.Failed)
const expired = computed(() => status.value === DataExportStatus.Expired)

// The date the export was made: when it finished, else when it was asked for.
const exportDate = computed(() => formatDate(latest.value?.readyAt ?? latest.value?.createdAt))
const sizeLabel = computed(() => {
  const size = latest.value?.sizeBytes
  return size == null ? '' : ` (${formatFileSize(size)})`
})
const waiting = computed(() => !!nextRequestAt.value && nextRequestAt.value.getTime() > Date.now())

const canRequest = computed(() => !preparing.value && !waiting.value && !requesting.value)

function notifyFailure(message: string) {
  $q.notify({ color: 'negative', textColor: 'white', icon: 'mdi-alert', message })
}

async function requestExport() {
  const result = await store.request()
  if (!result.ok) notifyFailure(result.message)
}

async function downloadExport() {
  if (!latest.value?.id) return
  const result = await store.download(latest.value.id)
  if (!result.ok) notifyFailure(result.message)
}

onMounted(() => store.load())
</script>

<template>
  <q-card flat bordered id="your-data" class="settings-card" data-testid="your-data-card">
    <q-card-section>
      <div class="text-h6">Your data</div>
    </q-card-section>

    <q-card-section v-if="!loaded" class="row items-center q-gutter-sm">
      <q-spinner size="20px" color="primary" />
      <span>Checking your exports…</span>
    </q-card-section>

    <q-card-section
      v-else-if="loadFailed"
      class="column items-start q-gutter-sm"
      data-testid="your-data-load-failed"
    >
      <div>We couldn't check your exports.</div>
      <q-btn
        outline
        color="primary"
        label="Retry"
        data-testid="your-data-load-retry"
        @click="store.load()"
      />
    </q-card-section>

    <q-card-section v-else class="column items-start q-gutter-sm">
      <!-- Ready: the download, how long it lasts, and when another can be asked for -->
      <template v-if="ready">
        <div data-testid="your-data-ready">
          Last export: {{ exportDate }} <q-icon name="mdi-check-circle" color="positive" />
        </div>
        <q-btn
          unelevated
          color="primary"
          icon="mdi-download"
          :label="`Download ZIP${sizeLabel}`"
          :loading="downloading"
          data-testid="your-data-download-button"
          @click="downloadExport"
        />
        <div class="text-caption">Available until {{ formatDate(latest?.expiresAt) }}</div>
      </template>

      <!-- Preparing -->
      <div
        v-else-if="preparing"
        class="row items-start no-wrap q-gutter-sm"
        data-testid="your-data-preparing"
      >
        <q-spinner size="20px" color="primary" />
        <span>Preparing your export. We'll let you know when it's ready.</span>
      </div>

      <!-- Failed -->
      <div
        v-else-if="failed"
        class="row items-start no-wrap q-gutter-sm"
        data-testid="your-data-failed"
      >
        <q-icon name="mdi-alert-circle" color="negative" size="20px" />
        <span>We couldn't prepare your export.</span>
      </div>

      <!-- Nothing yet, or the last one expired -->
      <template v-else>
        <div>Get a copy of everything we hold about you, as a ZIP file.</div>
        <div v-if="expired" class="text-caption" data-testid="your-data-expired">
          Your export from {{ exportDate }} has expired.
        </div>
      </template>

      <q-btn
        v-if="failed"
        unelevated
        color="primary"
        label="Try again"
        :disable="!canRequest"
        :loading="requesting"
        data-testid="your-data-retry-button"
        @click="requestExport"
      />
      <q-btn
        v-else-if="!ready || canRequest"
        :unelevated="!ready"
        :outline="ready"
        color="primary"
        :label="ready ? 'Request a new export' : 'Download my data'"
        :disable="!canRequest"
        :loading="requesting"
        data-testid="your-data-request-button"
        @click="requestExport"
      />

      <div v-if="waiting && !preparing" class="text-caption" data-testid="your-data-next">
        You can request another after {{ formatDateTime(nextRequestAt) }}
      </div>
    </q-card-section>
  </q-card>
</template>
